/**
 * Hook for on-device voice recording via the tauri-plugin-voice bridge.
 *
 * Owns the full microphone permission handshake, the start/stop
 * lifecycle, playback state, and elapsed-time tracking. On desktop
 * and web it returns a permission state of "granted" and lets the
 * start/stop/play calls fail softly so the same component can render
 * in both environments.
 *
 * The elapsed timer is driven by a 1 Hz interval — good enough for a
 * UX readout, cheap enough that we don't need requestAnimationFrame.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { invoke } from "@tauri-apps/api/core";

import type {
  VoicePermissionState,
  VoicePermissionStatus,
  VoiceRecording,
} from "@/types/voice";

interface UseVoiceRecordingResult {
  permission: VoicePermissionState | null;
  requestPermission: () => Promise<VoicePermissionState>;
  isRecording: boolean;
  elapsedSeconds: number;
  lastRecording: VoiceRecording | null;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<VoiceRecording | null>;
  isPlaying: boolean;
  play: (path: string) => Promise<void>;
  stopPlayback: () => Promise<void>;
}

const isNative =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export function useVoiceRecording(): UseVoiceRecordingResult {
  const [permission, setPermission] = useState<VoicePermissionState | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [lastRecording, setLastRecording] = useState<VoiceRecording | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isNative) {
      setPermission("granted");
      return;
    }

    let cancelled = false;
    const check = async () => {
      try {
        const status = await invoke<VoicePermissionStatus>(
          "plugin:voice|check_permissions"
        );
        if (!cancelled) setPermission(status.status);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    };
    check();

    return () => {
      cancelled = true;
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<VoicePermissionState> => {
    if (!isNative) {
      setPermission("granted");
      return "granted";
    }
    try {
      const status = await invoke<VoicePermissionStatus>(
        "plugin:voice|request_permissions"
      );
      setPermission(status.status);
      return status.status;
    } catch (err) {
      setError(String(err));
      return "denied";
    }
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (!isNative) {
      setError("Voice capture is only available on iOS.");
      return;
    }
    try {
      await invoke("plugin:voice|start_recording");
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      setIsRecording(true);

      tickRef.current = setInterval(() => {
        if (startedAtRef.current !== null) {
          setElapsedSeconds(
            Math.floor((Date.now() - startedAtRef.current) / 1000)
          );
        }
      }, 1000);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  const stop = useCallback(async (): Promise<VoiceRecording | null> => {
    if (!isNative) return null;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    try {
      const result = await invoke<VoiceRecording>(
        "plugin:voice|stop_recording"
      );
      setLastRecording(result);
      setIsRecording(false);
      startedAtRef.current = null;
      return result;
    } catch (err) {
      setError(String(err));
      setIsRecording(false);
      startedAtRef.current = null;
      return null;
    }
  }, []);

  /**
   * Play the recording at path. Resolves when playback ends naturally
   * or when stopPlayback is called — the plugin returns a single
   * completion whichever way playback ends.
   */
  const play = useCallback(async (path: string) => {
    if (!isNative) {
      setError("Playback is only available on iOS.");
      return;
    }
    setError(null);
    setIsPlaying(true);
    try {
      await invoke("plugin:voice|play_recording", { path });
    } catch (err) {
      setError(String(err));
    } finally {
      setIsPlaying(false);
    }
  }, []);

  const stopPlayback = useCallback(async () => {
    if (!isNative) return;
    try {
      await invoke("plugin:voice|stop_playback");
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  return {
    permission,
    requestPermission,
    isRecording,
    elapsedSeconds,
    lastRecording,
    error,
    start,
    stop,
    isPlaying,
    play,
    stopPlayback,
  };
}
